import {
  Activity,
  CalendarCheck2,
  CalendarPlus,
  CreditCard,
  PencilLine,
  Trophy,
  XCircle,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import { COMPETITION_STATUS_LABELS } from '../model/labels';
import type { CompetitionStatus } from '../model/types';

/**
 * Znak rozgrywki w wierszu listy turniejów — kwadrat z ikoną statusu.
 *
 * Kolor i ikona idą za statusem, a nie za dyscypliną: na liście turniejów
 * klubu w jednej dyscyplinie kolor per dyscyplina nie różnicowałby niczego,
 * a status jest tym, czego organizator szuka wzrokiem.
 */

/** Ikona przypisana statusowi rozgrywki. */
const STATUS_ICON: Record<CompetitionStatus, ReactNode> = {
  DRAFT: <PencilLine />,
  AWAITING_PAYMENT: <CreditCard />,
  REGISTRATION_OPEN: <CalendarPlus />,
  REGISTRATION_CLOSED: <CalendarCheck2 />,
  IN_PROGRESS: <Activity />,
  COMPLETED: <Trophy />,
  CANCELLED: <XCircle />,
};

/**
 * Klasy akcentu wypisane wprost, a nie składane z szablonu — Tailwind skanuje
 * źródła tekstowo i klasa zbudowana w czasie wykonania nie trafiłaby do arkusza.
 */
const STATUS_TILE_CLASS: Record<CompetitionStatus, string> = {
  DRAFT: 'bg-accent-orange/12 text-accent-orange border-accent-orange/25',
  AWAITING_PAYMENT: 'bg-accent-yellow/12 text-accent-yellow border-accent-yellow/25',
  REGISTRATION_OPEN: 'bg-accent-blue/12 text-accent-blue border-accent-blue/25',
  REGISTRATION_CLOSED: 'bg-accent-teal/12 text-accent-teal border-accent-teal/25',
  IN_PROGRESS: 'bg-accent-lime/14 text-accent-lime border-accent-lime/30',
  COMPLETED: 'bg-accent-green/12 text-accent-green border-accent-green/25',
  CANCELLED: 'bg-accent-coral/12 text-accent-coral border-accent-coral/25',
};

export interface TournamentIconProps {
  status: CompetitionStatus;
  className?: string;
}

/** Kwadratowy znak rozgrywki z ikoną odpowiadającą jej statusowi. */
export function TournamentIcon({ status, className }: TournamentIconProps) {
  return (
    <span
      role="img"
      aria-label={COMPETITION_STATUS_LABELS[status] ?? status}
      className={cn(
        'grid size-12 shrink-0 place-items-center rounded-xl border [&_svg]:size-5',
        STATUS_TILE_CLASS[status],
        className,
      )}
    >
      {STATUS_ICON[status]}
    </span>
  );
}
